import { createReducer } from 'deox';
import { produce } from 'immer';

import { createArtwork, deleteArtwork, updateArtwork } from 'modules/artwork/actions';

import { searchFilterChanged } from 'utils';

import { getArtworks, sortArtworks, searchArtwork, resetArtworksFilters } from './actions';
import { ArtworksState } from './types';

const initialState: ArtworksState = {
  loading: false,
  loadingMore: false,
  search: '',
  ordering: 'created',
  companies: {},
};

export const artworksReducer = createReducer(initialState, (handleAction) => [
  handleAction(getArtworks.request, (state, { payload }) =>
    produce(state, (draft) => {
      if (!draft.companies[payload.companyId]) {
        draft.loading = true;
        draft.companies[payload.companyId] = {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      } else {
        draft.loadingMore = true;
      }
    }),
  ),
  handleAction(getArtworks.success, (state, { payload }) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
      draft.companies[payload.companyId] = {
        ...payload.response,
        results: [...draft.companies[payload.companyId].results, ...payload.response.results],
      };
    }),
  ),
  handleAction(getArtworks.fail, (state) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
    }),
  ),
  handleAction(searchArtwork.request, (state, { payload }) =>
    produce(state, (draft) => {
      if (searchFilterChanged(draft.search, payload.search)) {
        draft.loading = true;
        draft.companies[payload.companyId] = {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      }
    }),
  ),
  handleAction(searchArtwork.success, (state, { payload }) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
      draft.search = payload.search;
      draft.companies[payload.companyId] = {
        ...payload.response,
        results: [...draft.companies[payload.companyId].results, ...payload.response.results],
      };
    }),
  ),
  handleAction(searchArtwork.fail, (state) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
    }),
  ),
  handleAction(sortArtworks.request, (state, { payload }) =>
    produce(state, (draft) => {
      if (draft.ordering !== payload.ordering) {
        draft.loading = true;
        draft.companies[payload.companyId] = {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      }
    }),
  ),
  handleAction(sortArtworks.success, (state, { payload }) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
      draft.ordering = payload.ordering;
      draft.companies[payload.companyId] = {
        ...payload.response,
        results: [...draft.companies[payload.companyId].results, ...payload.response.results],
      };
    }),
  ),
  handleAction(sortArtworks.fail, (state) =>
    produce(state, (draft) => {
      draft.loading = false;
      draft.loadingMore = false;
    }),
  ),
  handleAction(resetArtworksFilters, (state, { payload }) =>
    produce(state, (draft) => {
      if (draft.search || draft.ordering !== 'created') {
        draft.search = '';
        draft.ordering = 'created';
        delete draft.companies[payload];
      }
    }),
  ),
  handleAction(createArtwork.success, (state, { payload }) =>
    produce(state, (draft) => {
      const company = draft.companies[payload.companyId];

      if (draft.ordering === 'created' || draft.ordering === 'type') {
        if (company.count === company.results.length) {
          company.count += 1;
          company.results.push(payload);
        }
      } else if (draft.ordering === 'name') {
        const lastArtwork = company.results[company.results.length - 1];

        if (lastArtwork.name <= payload.name) {
          if (company.count === company.results.length) {
            company.count += 1;
            company.results.push(payload);
          }
        } else {
          company.count += 1;
          company.results.push(payload);
          company.results = company.results.sort((a, b) => (a.name > b.name ? 1 : -1));
        }
      }
    }),
  ),
  handleAction(deleteArtwork.success, (state, { payload }) =>
    produce(state, (draft) => {
      const company = draft.companies[payload.companyId];
      company.count -= 1;
      company.results = company.results.filter((artwork) => artwork.id !== payload.artworkId);
    }),
  ),
  handleAction(updateArtwork.success, (state, { payload }) =>
    produce(state, (draft) => {
      const company = draft.companies[payload.companyId];
      company.results = company.results.map((artwork) => {
        if (artwork.id === payload.id) {
          return payload;
        }

        return artwork;
      });
    }),
  ),
]);
